import {
  ApiUser,
  AuthUsersListResponse,
  UsersListResponse,
} from "../Interfaces/UsersInterface";
import { AxiosError } from "axios";
import { apiClient } from "./auth";

export interface GetUsersApiParams {
  page?: number;
  size?: number;
  q?: string;
  /** Comma-separated role IDs for server-side filter (e.g. "SUPER_ADMIN,TENANT_ADMIN") */
  roles?: string;
  teamLeaderId?: string;
  status?: string;
  teamType?: string;
}

export interface UsersSeekResponse {
  items: ApiUser[];
  nextCursor?: string;
  size: number;
}

/**
 * Payload for POST /auth/register (add user).
 */
export interface RegisterUserPayload {
  email: string;
  password: string;
  phone?: string;
  role: string;
  tenantId: string;
  facilityId?: string;
  twoFactorEnabled?: boolean;
  representative?: {
    fullName?: string;
    location?: string;
    departmentId?: string;
    hospitalName?: string;
    doctorName?: string;
    unit?: string;
    region?: string;
  };
}

export interface RegisterUserResponse {
  ok: boolean;
  id: string;
}

/**
 * Registers a new user (add user).
 * Calls POST /auth/register.
 */
export const registerUserApi = async (
  payload: RegisterUserPayload
): Promise<RegisterUserResponse> => {
  const response = await apiClient.post<
    RegisterUserPayload,
    RegisterUserResponse
  >("/auth/register", payload);
  return response.data;
};

/**
 * Fetches a page of users from the API.
 * @param params - page, size, optional q (search)
 * @returns Promise<UsersListResponse> - items, total, page, size
 */
export const getUsersApi = async (
  params?: GetUsersApiParams
): Promise<UsersListResponse> => {
  const page = params?.page ?? 1;
  const size = params?.size ?? 20;
  const queryParams: Record<string, number | string> = { page, size };
  if (params?.q?.trim()) queryParams.q = params.q.trim();
  if (params?.roles?.trim()) queryParams.roles = params.roles.trim();
  if (params?.teamLeaderId?.trim())
    queryParams.teamLeaderId = params.teamLeaderId.trim();
  if (params?.status?.trim() === "ACTIVE") queryParams.isActive = "true";
  else if (params?.status?.trim() === "INACTIVE")
    queryParams.isActive = "false";
  if (params?.teamType?.trim()) queryParams.teamType = params.teamType.trim();
  const response = await apiClient.get<UsersListResponse>("/auth/users/page", {
    params: queryParams,
  });
  return response.data;
};

/** Slim user projection from GET /auth/users/lookup (agent-accessible). */
export interface ApiUserLookup {
  _id: string;
  email: string;
  roles: string[];
  isActive: boolean;
  representative?: { fullName?: string };
}

export interface UsersLookupResponse {
  items: ApiUserLookup[];
  total: number;
  page: number;
  size: number;
}

/**
 * Agent-accessible user lookup for assignee dropdowns (read-only).
 * GET /auth/users/lookup — slimmer projection than /auth/users/page.
 */
export const getUsersLookupApi = async (
  params?: GetUsersApiParams
): Promise<UsersLookupResponse> => {
  const page = params?.page ?? 1;
  const size = params?.size ?? 20;
  const queryParams: Record<string, number | string> = { page, size };
  if (params?.q?.trim()) queryParams.q = params.q.trim();
  if (params?.roles?.trim()) queryParams.roles = params.roles.trim();
  if (params?.teamLeaderId?.trim())
    queryParams.teamLeaderId = params.teamLeaderId.trim();
  if (params?.status?.trim() === "ACTIVE") queryParams.isActive = "true";
  else if (params?.status?.trim() === "INACTIVE")
    queryParams.isActive = "false";
  if (params?.teamType?.trim()) queryParams.teamType = params.teamType.trim();
  const response = await apiClient.get<UsersLookupResponse>(
    "/auth/users/lookup",
    {
      params: queryParams,
    }
  );
  return response.data;
};

/** Backend PageQueryDTO caps size at 100. Ask for that so one page covers small tenants. */
const AUTH_USERS_PAGE_SIZE = 100;
/** Stop if total is missing or the server repeats a page. 100 pages is 10_000 users. */
const AUTH_USERS_MAX_PAGES = 100;

/**
 * Fetches every page of GET /auth/users.
 * The route is paged (default size 20). Callers need the full list for assignee
 * and owner pickers, so this follows `total` until every page is loaded.
 * Uses apiClient so a stale access token runs the normal refresh + retry path.
 * (A raw axios call used the token from Redux at request time only; the backend
 * then returned 401 with "Session revoked" while other app calls still worked.)
 */
export const getAuthUsersListApi = async (): Promise<AuthUsersListResponse> => {
  const items: ApiUser[] = [];
  const seenIds = new Set<string>();

  try {
    for (let page = 1; page <= AUTH_USERS_MAX_PAGES; page += 1) {
      const response = await apiClient.get<
        AuthUsersListResponse & UsersListResponse
      >("/auth/users", { params: { page, size: AUTH_USERS_PAGE_SIZE } });
      const data = response.data;
      const pageItems = Array.isArray(data?.items) ? data.items : [];
      let added = 0;
      for (const user of pageItems) {
        const id = user?._id;
        if (!id || seenIds.has(id)) continue;
        seenIds.add(id);
        items.push(user);
        added += 1;
      }

      const total = typeof data?.total === "number" ? data.total : items.length;
      const pageSize =
        typeof data?.size === "number" && data.size > 0
          ? data.size
          : pageItems.length;

      if (
        pageItems.length === 0 ||
        added === 0 ||
        items.length >= total ||
        pageItems.length < pageSize
      ) {
        break;
      }
    }

    return { items };
  } catch (error: unknown) {
    const status = (error as AxiosError)?.response?.status;
    if (status === 403) {
      return { items: [] };
    }
    throw error;
  }
};

/**
 * Search users (seek). Uses GET /auth/users/seek with q, size, and optional roles.
 * @param query - search string
 * @param limit - max results (default 20)
 * @param roles - optional comma-separated role filter (e.g. "SUPER_ADMIN,TENANT_ADMIN")
 */
export const getUsersSeekApi = async (
  query: string,
  limit?: number,
  roles?: string,
  teamLeaderId?: string,
  status?: string,
  teamType?: string
): Promise<UsersSeekResponse> => {
  const q = query.trim();
  const size = limit ?? 20;
  const params: Record<string, string | number> = { q, size };
  if (roles?.trim()) params.roles = roles.trim();
  if (teamLeaderId?.trim()) params.teamLeaderId = teamLeaderId.trim();
  if (status?.trim() === "ACTIVE") params.isActive = "true";
  else if (status?.trim() === "INACTIVE") params.isActive = "false";
  if (teamType?.trim()) params.teamType = teamType.trim();
  const response = await apiClient.get<UsersSeekResponse>("/auth/users/seek", {
    params,
  });
  return response.data;
};

export interface ResetUserPasswordPayload {
  userId: string;
  newPassword: string;
}

/**
 * Admin reset user password
 * Calls PATCH /auth/admin/users/:userId/password
 */
// export const resetUserPasswordApi = async (
//   payload: ResetUserPasswordPayload
// ): Promise<{ ok: boolean }> => {
//   const { userId, newPassword } = payload;
//   const response = await apiClient.patch<ResetUserPasswordPayload, { ok: boolean }>(
//     `/auth/admin/users/${userId}/password`,
//     { newPassword }
//   );
//   return response.data;
// };

/** Reassignment result from revoke/delete when toUserId is provided. */
export interface ReassignmentResult {
  totalOpportunities: number;
  reassigned: number;
  failed: number;
}

/** Revert result from set active when temporary deactivation was reverted. */
export interface RevertResult {
  totalOpportunities: number;
  reverted: number;
  failed: number;
}

/**
 * Payload for deactivating a user (revoke).
 * See user-deactivation-reassignment API: PATCH /auth/users/:id/revoke
 */
export interface RevokeUserPayload {
  /** User to receive reassigned opportunities. Required for mode "temporary". */
  toUserId?: string;
  /** "permanent" (default) or "temporary". */
  mode?: "permanent" | "temporary";
  /** Required for temporary. ISO date (YYYY-MM-DD). */
  startDate?: string;
  /** Required for temporary. ISO date (YYYY-MM-DD), must be after startDate. */
  endDate?: string;
}

/**
 * Admin set user active (reactivate).
 * Calls PATCH /auth/admin/users/:userId/active
 */
export const setUserActiveApi = async (
  userId: string
): Promise<{ ok?: boolean; revertResult?: RevertResult }> => {
  const response = await apiClient.patch<
    unknown,
    { ok?: boolean; revertResult?: RevertResult }
  >(`/auth/admin/users/${userId}/active`, {});
  return response.data;
};

/**
 * Admin revoke user (deactivate – temporary or permanent).
 * Calls PATCH /auth/admin/users/:userId/revoke
 */
export const revokeUserApi = async (
  userId: string,
  payload?: RevokeUserPayload
): Promise<{ ok?: boolean; reassignment?: ReassignmentResult }> => {
  const body = payload ?? {};
  const response = await apiClient.patch<
    RevokeUserPayload,
    { ok?: boolean; reassignment?: ReassignmentResult }
  >(`/auth/admin/users/${userId}/revoke`, body);
  return response.data;
};

/**
 * Admin enable 2FA for a user.
 * Calls POST /auth/admin/users/:userId/2fa/enable
 */
export interface User2FaToggleResponse {
  twoFactorEnabled: boolean;
}

export const enableUser2FaApi = async (
  userId: string
): Promise<User2FaToggleResponse> => {
  const response = await apiClient.post<unknown, User2FaToggleResponse>(
    `/auth/admin/users/${userId}/2fa/enable`,
    {}
  );
  return response.data;
};

/**
 * Admin disable 2FA for a user.
 * Calls POST /auth/admin/users/:userId/2fa/disable
 */
export const disableUser2FaApi = async (
  userId: string
): Promise<User2FaToggleResponse> => {
  const response = await apiClient.post<unknown, User2FaToggleResponse>(
    `/auth/admin/users/${userId}/2fa/disable`,
    {}
  );
  return response.data;
};

/**
 * Admin fetch 2FA status for a user.
 * Calls GET /auth/admin/users/:userId/2fa/status
 */
export interface User2FaStatusResponse {
  userId: string;
  twoFactorEnabled: boolean;
}

export const getUser2FaStatusApi = async (
  userId: string
): Promise<User2FaStatusResponse> => {
  const response = await apiClient.get<User2FaStatusResponse>(
    `/auth/admin/users/${userId}/2fa/status`
  );
  return response.data;
};

/**
 * Admin delete user (admin). Optional reassignment via toUserId.
 * Calls DELETE /auth/admin/admins/:userId
 */
export const deleteAdminUserApi = async (
  userId: string,
  payload?: { toUserId?: string }
): Promise<{ ok?: boolean; reassignment?: ReassignmentResult }> => {
  const res = await apiClient.delete<{
    ok?: boolean;
    reassignment?: ReassignmentResult;
  }>(`/auth/admin/admins/${userId}`, payload ? { data: payload } : undefined);
  return res.data ?? { ok: true };
};

/**
 * Response shape for GET /auth/users/:userId/groups.
 * Matches backend: { success, data: { userId, permissionGroup?, routingGroups? } }
 */
export interface UserGroupsPermissionGroup {
  id: string;
  name?: string;
  [key: string]: unknown;
}

export interface UserGroupsRoutingGroup {
  id: string;
  name?: string;
  membership?: { id: string; [key: string]: unknown };
  [key: string]: unknown;
}

export interface UserGroupsResponse {
  success?: boolean;
  data?: {
    userId?: string;
    permissionGroup?: UserGroupsPermissionGroup | null;
    routingGroups?: UserGroupsRoutingGroup[] | null;
  };
}

/**
 * Fetches permission and routing group assignments for a user.
 * Calls GET /auth/users/:userId/groups.
 * Returns the full response body; use result.data.permissionGroup?.id and result.data.routingGroups?.[0]?.id.
 */
export const getUserGroupsApi = async (
  userId: string
): Promise<UserGroupsResponse> => {
  const response = await apiClient.get<UserGroupsResponse>(
    `/auth/users/${userId}/groups`
  );
  return response.data;
};

/** SPOC operation department membership (junction record). */
export interface SpocOperationDepartmentMembership {
  id: string;
  operationDepartmentId: string;
  facilityId: string;
  userId: string;
  userName: string;
  isActive: boolean;
  joinedAt: string;
}

export interface UserOperationDepartmentsResponse {
  success?: boolean;
  data?: SpocOperationDepartmentMembership[];
}

/**
 * Fetches operation department memberships for a SPOC user.
 * Calls GET /auth/users/:userId/operation-departments.
 */
export const getUserOperationDepartmentsApi = async (
  userId: string
): Promise<SpocOperationDepartmentMembership[]> => {
  const response = await apiClient.get<UserOperationDepartmentsResponse>(
    `/auth/users/${userId}/operation-departments`
  );
  return response.data?.data ?? [];
};

/**
 * Payload for PATCH /auth/users/:id (update user).
 * Matches backend UpdateUserDTO: all fields optional.
 */
export interface UpdateUserPayload {
  email?: string;
  phone?: string;
  role?: string;
  isActive?: boolean;
  representative?: {
    fullName?: string;
    location?: string;
    teamLeadId?: string;
    departmentId?: string;
    hospitalName?: string;
    doctorName?: string;
    unit?: string;
    region?: string;
  };
}

/**
 * Updates an existing user.
 * Calls PATCH /auth/users/:id.
 */
export const updateUserApi = async (
  userId: string,
  payload: UpdateUserPayload
): Promise<{ ok: boolean }> => {
  const response = await apiClient.patch<UpdateUserPayload, { ok: boolean }>(
    `/auth/users/${userId}`,
    payload
  );
  return response.data;
};
